/**
 * OldMortgageeSelect — the `old_mortgagee_name` control for Change/Delete.
 *
 * Successor to InterestTargetPicker (form redesign 2026-08-03): the AM now
 * picks the OLD mortgagee by name directly on the `old_mortgagee_name` field,
 * and `target_interest` no longer renders as a field at all — the picked row
 * still travels on submit (this control writes both keys), but its grid
 * details surface here as a details panel that pops in while hovering or
 * focusing an option, instead of as a separate form field.
 *
 * Rendered as a `fieldOverrides` entry for `old_mortgagee_name`, mirroring
 * `PolicyNumberCombobox`. Mounted whenever the selected policy carries ANY
 * interest options (a single option still needs the derived control and its
 * details popout — the name must match EPIC exactly, and this popout is the
 * only place the grid details surface). Only with none/unknown does the
 * field stay a plain text input — the warehouse-lags-EPIC escape hatch.
 *
 * Deliberately a button-and-listbox rather than a native <select>: native
 * options cannot carry a hover details panel, and a custom listbox commits by
 * click — the one interaction jsdom can actually drive (typing into MUI
 * autocompletes is untestable here; see the jest landmine notes).
 */

import { useEffect, useRef, useState } from "react";
import CustomTooltip from "~/common/components/CustomTooltip";
import type { FieldSchema } from "~/common/workflows/schemaHelpers";
import {
  COMPACT_FIELD_LABEL,
  COMPACT_SINGLE_LINE,
  LABEL_INLINE_RESET,
} from "~/common/workflows/launch/launchDensity";
import { TYPE_BODY } from "~/common/workflows/paneTypography";
import {
  type InterestOption,
  interestOptionLabel,
} from "./interestTargetOptions";

export interface OldMortgageeSelectProps {
  field: FieldSchema;
  /** Interests on the SELECTED policy line only. */
  options: readonly InterestOption[];
  /** Currently picked row, or null when none is resolved. */
  target: InterestOption | null;
  /** Current free-text value of `old_mortgagee_name`. */
  name: string;
  disabled?: boolean;
  /** A row was picked: write BOTH old_mortgagee_name and target_interest. */
  onSelect: (opt: InterestOption) => void;
  /** "Not listed" — clears the target and reverts to manual entry. */
  onNotListed: () => void;
  /** Manual-mode typing: updates old_mortgagee_name only. */
  onManualChange: (name: string) => void;
}

/** One row's grid details, shown in the pop-in panel and nowhere else. */
function OptionDetails({ opt }: { opt: InterestOption }) {
  const cityLine = [opt.city, opt.state_code].filter(Boolean).join(", ");
  const rows: Array<[string, string]> = [
    ["Name", opt.name || "—"],
    ["Loan #", opt.loan_number || "—"],
    ["Address", opt.address1 || "—"],
    ["City / State", cityLine || "—"],
    ["ZIP", opt.postal_code || "—"],
  ];
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
      {rows.map(([k, v]) => (
        <div key={k} className="contents">
          <dt className="text-xs text-primary-gray-light-500">{k}</dt>
          {/* The full value when it truncates, in ora's tooltip (story 28633). */}
          <CustomTooltip
            id={`old-mortgagee-detail-${k}`}
            title={v}
            placement="top-start"
            arrow
            isOnModal
          >
            <dd className="text-xs text-primary-gray-light-800 truncate">{v}</dd>
          </CustomTooltip>
        </div>
      ))}
    </dl>
  );
}

export default function OldMortgageeSelect({
  field,
  options,
  target,
  name,
  disabled,
  onSelect,
  onNotListed,
  onManualChange,
}: OldMortgageeSelectProps) {
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState<InterestOption | null>(null);
  // ALWAYS start in list mode (operator correction 2026-08-03): the old
  // name must match EPIC exactly, which is why this stopped being free
  // text in the first place — an email-extracted name the server could
  // not resolve to a row is precisely the value that must NOT be typed
  // through as-is. Manual entry exists only behind the explicit
  // "Not listed" escape; the extracted name stays in `values` untouched
  // until the AM picks or opts out.
  const [manual, setManual] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const labelEl = (
    <label htmlFor="old-mortgagee-select" className={COMPACT_FIELD_LABEL}>
      {field.label}
      {field.required ? <span aria-hidden="true" className={LABEL_INLINE_RESET}> *</span> : null}
    </label>
  );

  if (manual) {
    return (
      <div className="flex flex-col gap-0.5">
        {labelEl}
        <input
          id="old-mortgagee-select"
          type="text"
          value={name}
          disabled={disabled}
          onChange={(e) => onManualChange(e.target.value)}
          placeholder="Type the mortgagee's name as it appears in EPIC"
          className={`touch-target ${COMPACT_SINGLE_LINE}`}
          data-testid="old-mortgagee-manual-input"
        />
        <button
          type="button"
          disabled={disabled}
          className={`inline-flex touch-target items-center self-start ${TYPE_BODY} text-primary-gray-light-700 underline disabled:opacity-50`}
          onClick={() => setManual(false)}
          data-testid="old-mortgagee-back-to-list"
        >
          Choose from the policy&apos;s mortgagees instead
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-0.5" ref={rootRef}>
      {labelEl}
      <button
        id="old-mortgagee-select"
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={`flex touch-target items-center justify-between gap-2 text-left ${COMPACT_SINGLE_LINE}`}
        data-testid="old-mortgagee-trigger"
      >
        <span className={`min-w-0 truncate ${target ? "text-primary-gray-light-900" : "text-primary-gray-light-500"}`}>
          {target
            ? interestOptionLabel(target)
            : "Select the existing mortgagee…"}
        </span>
        <span aria-hidden="true" className="flex-shrink-0 text-primary-gray-light-500">
          ▾
        </span>
      </button>
      {open && (
        <div className="relative">
          <div
            role="listbox"
            aria-label={field.label}
            className="absolute z-20 mt-1 w-full rounded-lg border border-primary-gray-light-300 bg-base-white shadow-lg"
            data-testid="old-mortgagee-listbox"
          >
            <ul className="max-h-48 overflow-y-auto">
              {options.map((o) => (
                <li key={o.interest_id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={target?.interest_id === o.interest_id}
                    // One step darker than the original 100/50, which were so
                    // close to white that neither hover nor selection read as a
                    // state change. Staying in the LIGHT end of the ramp is
                    // deliberate: a dark fill was tried and is too heavy for a
                    // list that also drives the hover details panel beside it.
                    // 200 hover over 100 selected keeps the two distinguishable
                    // while the pointer is on the selected row.
                    className={`flex touch-target w-full items-center px-3 py-1.5 text-left text-sm hover:bg-primary-gray-light-200 ${
                      target?.interest_id === o.interest_id
                        ? "bg-primary-gray-light-100 font-medium"
                        : ""
                    }`}
                    onMouseEnter={() => setHovered(o)}
                    onFocus={() => setHovered(o)}
                    onMouseLeave={() =>
                      setHovered((h) => (h?.interest_id === o.interest_id ? null : h))
                    }
                    onClick={() => {
                      onSelect(o);
                      setOpen(false);
                    }}
                    data-testid={`old-mortgagee-option-${o.interest_id}`}
                  >
                    <span className="min-w-0 truncate">{interestOptionLabel(o)}</span>
                  </button>
                </li>
              ))}
              <li>
                {/* Escape hatch: the warehouse can lag EPIC, so the AM must
                    always be able to proceed when the mortgagee they mean is
                    not on this list. Clears the target; the downstream picker
                    HITL stays the backstop. */}
                <button
                  type="button"
                  role="option"
                  aria-selected={false}
                  className="flex touch-target w-full items-center border-t border-primary-gray-light-200 px-3 py-1.5 text-left text-sm text-primary-gray-light-600 hover:bg-primary-gray-light-200"
                  onClick={() => {
                    onNotListed();
                    setManual(true);
                    setOpen(false);
                  }}
                  data-testid="old-mortgagee-not-listed"
                >
                  Not listed — enter manually
                </button>
              </li>
            </ul>
            {hovered && (
              <div
                className="border-t border-primary-gray-light-200 bg-primary-gray-light-50 px-3 py-1.5"
                data-testid="old-mortgagee-details"
              >
                <OptionDetails opt={hovered} />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
